import { Type, type Static } from '@sinclair/typebox';

import {
  ASSISTANT_STATUSES,
  CONVERSATION_STATUSES,
  CONVERSATIONS_PAGE_MAX,
  DIMMITRI_ROLES,
  FALLBACK_ACTIONS,
  LLM_PROVIDERS,
  MESSAGE_ROLES,
  PENDING_ACTION_STATUSES,
  PENDING_ACTION_TYPES,
  PLANS,
  PROVIDER_MESSAGE_MAX_LENGTH,
  UNANSWERED_STATUSES,
} from './constants.js';

const PlanSchema = Type.Union(PLANS.map((p) => Type.Literal(p)));

const AssistantStatusSchema = Type.Union(ASSISTANT_STATUSES.map((s) => Type.Literal(s)));
const LLMProviderSchema = Type.Union(LLM_PROVIDERS.map((p) => Type.Literal(p)));
const FallbackActionSchema = Type.Union(FALLBACK_ACTIONS.map((f) => Type.Literal(f)));

export const EmailSchema = Type.String({
  format: 'email',
  minLength: 3,
  maxLength: 254,
});

export const PasswordSchema = Type.String({
  minLength: 10,
  maxLength: 200,
});

export const RegisterBodySchema = Type.Object({
  email: EmailSchema,
  password: PasswordSchema,
  displayName: Type.String({ minLength: 1, maxLength: 255 }),
});
export type RegisterBody = Static<typeof RegisterBodySchema>;

export const LoginBodySchema = Type.Object({
  email: EmailSchema,
  password: Type.String({ minLength: 1, maxLength: 200 }),
});
export type LoginBody = Static<typeof LoginBodySchema>;

export const MeResponseSchema = Type.Object({
  id: Type.Number(),
  email: Type.String(),
  displayName: Type.String(),
  plan: Type.Union([
    Type.Literal('free'),
    Type.Literal('starter'),
    Type.Literal('pro'),
    Type.Literal('business'),
  ]),
  createdAt: Type.String(),
  // True when this account is the hard-coded administrator (CLAUDE.md §6).
  isAdmin: Type.Boolean(),
  // True when an admin is currently impersonating this account in this tab.
  impersonating: Type.Boolean(),
});
export type MeResponse = Static<typeof MeResponseSchema>;

// Settings — update the connected account's profile (only the display name is
// editable in v1; email/plan changes go through dedicated flows).
export const UpdateAccountBodySchema = Type.Object({
  displayName: Type.String({ minLength: 1, maxLength: 255 }),
});
export type UpdateAccountBody = Static<typeof UpdateAccountBodySchema>;

// Settings — change the account password. `newPassword` reuses the registration
// strength rule; `currentPassword` only needs to be non-empty to be verified.
export const ChangePasswordBodySchema = Type.Object({
  currentPassword: Type.String({ minLength: 1, maxLength: 200 }),
  newPassword: PasswordSchema,
});
export type ChangePasswordBody = Static<typeof ChangePasswordBodySchema>;

// Password reset (« mot de passe oublié »). The request only carries an email;
// the endpoint answers identically whether or not it matched an account.
export const ForgotPasswordBodySchema = Type.Object({
  email: EmailSchema,
});
export type ForgotPasswordBody = Static<typeof ForgotPasswordBodySchema>;

// Completing a reset: the token from the emailed link plus the new password
// (same strength rule as registration).
export const ResetPasswordBodySchema = Type.Object({
  token: Type.String({ minLength: 1, maxLength: 255 }),
  password: PasswordSchema,
});
export type ResetPasswordBody = Static<typeof ResetPasswordBodySchema>;

// Response of GET /api/auth/reset-password/:token — whether the link is still
// usable, so the reset page can show the form or an "expired link" message.
export const ResetTokenStatusSchema = Type.Object({
  valid: Type.Boolean(),
});
export type ResetTokenStatus = Static<typeof ResetTokenStatusSchema>;

export const ApiErrorSchema = Type.Object({
  error: Type.Object({
    code: Type.String(),
    message: Type.String(),
    details: Type.Optional(Type.Unknown()),
  }),
});

// ── Assistants & knowledge sections ──────────────────────────────────────

export const AssistantResponseSchema = Type.Object({
  id: Type.Number(),
  accountId: Type.Number(),
  name: Type.String(),
  displayName: Type.String(),
  businessName: Type.String(),
  status: AssistantStatusSchema,
  llmProvider: LLMProviderSchema,
  llmModel: Type.String(),
  systemPrompt: Type.Union([Type.String(), Type.Null()]),
  greeting: Type.String(),
  fallbackAction: FallbackActionSchema,
  contactAfterNMessages: Type.Number(),
  createdAt: Type.String(),
  updatedAt: Type.String(),
});
export type AssistantResponse = Static<typeof AssistantResponseSchema>;

// List item — the assistant plus the lightweight counts shown on dashboard cards.
export const AssistantListItemSchema = Type.Composite([
  AssistantResponseSchema,
  Type.Object({
    conversationCount: Type.Number(),
    slugCount: Type.Number(),
    sectionCount: Type.Number(),
  }),
]);
export type AssistantListItem = Static<typeof AssistantListItemSchema>;

export const KnowledgeSectionResponseSchema = Type.Object({
  id: Type.Number(),
  assistantId: Type.Number(),
  accountId: Type.Number(),
  sectionKey: Type.String(),
  title: Type.String(),
  content: Type.String(),
  orderIndex: Type.Number(),
  updatedAt: Type.String(),
});
export type KnowledgeSectionResponse = Static<typeof KnowledgeSectionResponseSchema>;

export const AssistantDetailResponseSchema = Type.Composite([
  AssistantResponseSchema,
  Type.Object({ sections: Type.Array(KnowledgeSectionResponseSchema) }),
]);
export type AssistantDetailResponse = Static<typeof AssistantDetailResponseSchema>;

export const CreateAssistantBodySchema = Type.Object({
  name: Type.String({ minLength: 1, maxLength: 255 }),
  displayName: Type.String({ minLength: 1, maxLength: 255 }),
  businessName: Type.String({ minLength: 1, maxLength: 255 }),
  greeting: Type.String({ minLength: 1, maxLength: 2000 }),
  llmProvider: Type.Optional(LLMProviderSchema),
  llmModel: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
  fallbackAction: Type.Optional(FallbackActionSchema),
  contactAfterNMessages: Type.Optional(Type.Integer({ minimum: 0, maximum: 100 })),
});
export type CreateAssistantBody = Static<typeof CreateAssistantBodySchema>;

export const UpdateAssistantBodySchema = Type.Object({
  name: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
  displayName: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
  businessName: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
  greeting: Type.Optional(Type.String({ minLength: 1, maxLength: 2000 })),
  status: Type.Optional(AssistantStatusSchema),
  llmProvider: Type.Optional(LLMProviderSchema),
  llmModel: Type.Optional(Type.String({ minLength: 1, maxLength: 64 })),
  fallbackAction: Type.Optional(FallbackActionSchema),
  contactAfterNMessages: Type.Optional(Type.Integer({ minimum: 0, maximum: 100 })),
});
export type UpdateAssistantBody = Static<typeof UpdateAssistantBodySchema>;

export const CreateSectionBodySchema = Type.Object({
  sectionKey: Type.String({ minLength: 1, maxLength: 64 }),
  title: Type.String({ minLength: 1, maxLength: 255 }),
  content: Type.String({ maxLength: 20000 }),
  orderIndex: Type.Optional(Type.Integer({ minimum: 0, maximum: 1000 })),
});
export type CreateSectionBody = Static<typeof CreateSectionBodySchema>;

export const UpdateSectionBodySchema = Type.Object({
  title: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
  content: Type.Optional(Type.String({ maxLength: 20000 })),
  orderIndex: Type.Optional(Type.Integer({ minimum: 0, maximum: 1000 })),
});
export type UpdateSectionBody = Static<typeof UpdateSectionBodySchema>;

// ── Slugs (printed-on-physical-supports QR identifiers) ──────────────────

export const SlugResponseSchema = Type.Object({
  slug: Type.String(),
  accountId: Type.Number(),
  currentAssistantId: Type.Number(),
  label: Type.Union([Type.String(), Type.Null()]),
  createdAt: Type.String(),
  reassignedAt: Type.Union([Type.String(), Type.Null()]),
});
export type SlugResponse = Static<typeof SlugResponseSchema>;

export const CreateSlugBodySchema = Type.Object({
  assistantId: Type.Integer({ minimum: 1 }),
  label: Type.Optional(Type.String({ minLength: 1, maxLength: 255 })),
});
export type CreateSlugBody = Static<typeof CreateSlugBodySchema>;

export const ReassignSlugBodySchema = Type.Object({
  assistantId: Type.Integer({ minimum: 1 }),
});
export type ReassignSlugBody = Static<typeof ReassignSlugBodySchema>;

// ── Visitor chat (public) ────────────────────────────────────────────────

export const PublicMessageSchema = Type.Object({
  id: Type.Number(),
  role: Type.Union([Type.Literal('user'), Type.Literal('assistant'), Type.Literal('provider')]),
  authorName: Type.Union([Type.String(), Type.Null()]),
  content: Type.String(),
  createdAt: Type.String(),
});

// ── Dimmitri (méta-assistant) ────────────────────────────────────────────

const DimmitriRoleSchema = Type.Union(DIMMITRI_ROLES.map((r) => Type.Literal(r)));
const PendingActionTypeSchema = Type.Union(PENDING_ACTION_TYPES.map((t) => Type.Literal(t)));
const PendingActionStatusSchema = Type.Union(PENDING_ACTION_STATUSES.map((s) => Type.Literal(s)));

export const DimmitriMessageViewSchema = Type.Object({
  id: Type.Number(),
  role: DimmitriRoleSchema,
  content: Type.String(),
  toolName: Type.Union([Type.String(), Type.Null()]),
  createdAt: Type.String(),
});

export const DimmitriPendingActionViewSchema = Type.Object({
  id: Type.Number(),
  actionType: PendingActionTypeSchema,
  // `summary` is guaranteed; the rest of the payload varies per action type.
  payload: Type.Intersect([
    Type.Object({ summary: Type.String() }),
    Type.Record(Type.String(), Type.Unknown()),
  ]),
  status: PendingActionStatusSchema,
  createdAt: Type.String(),
  resolvedAt: Type.Union([Type.String(), Type.Null()]),
});

// The DimmitriConversationView *type* lives in types.ts (hand-written interface);
// this schema is its wire validator. Kept structurally in sync by hand.
export const DimmitriConversationViewSchema = Type.Object({
  conversationId: Type.Union([Type.Number(), Type.Null()]),
  messages: Type.Array(DimmitriMessageViewSchema),
  pendingActions: Type.Array(DimmitriPendingActionViewSchema),
});

export const DimmitriMessageBodySchema = Type.Object({
  conversationId: Type.Optional(Type.Integer({ minimum: 1 })),
  content: Type.String({ minLength: 1, maxLength: 8000 }),
});
export type DimmitriMessageBody = Static<typeof DimmitriMessageBodySchema>;

export const PublicAssistantPayloadSchema = Type.Object({
  slug: Type.String(),
  available: Type.Boolean(),
  assistant: Type.Object({
    displayName: Type.String(),
    businessName: Type.String(),
    greeting: Type.String(),
    contactAfterNMessages: Type.Number(),
  }),
  conversation: Type.Union([
    Type.Object({ id: Type.Number(), messages: Type.Array(PublicMessageSchema) }),
    Type.Null(),
  ]),
});

// ── Conversations (provider dashboard) ───────────────────────────────────

const ConversationStatusSchema = Type.Union(CONVERSATION_STATUSES.map((s) => Type.Literal(s)));
const MessageRoleSchema = Type.Union(MESSAGE_ROLES.map((r) => Type.Literal(r)));

export const ConversationListItemSchema = Type.Object({
  id: Type.Number(),
  assistantId: Type.Number(),
  assistantName: Type.String(),
  slug: Type.String(),
  visitorUuid: Type.String(),
  status: ConversationStatusSchema,
  messageCount: Type.Number(),
  startedAt: Type.String(),
  lastMessageAt: Type.String(),
  lastMessagePreview: Type.Union([Type.String(), Type.Null()]),
  lastMessageRole: Type.Union([MessageRoleSchema, Type.Null()]),
  hasUnanswered: Type.Boolean(),
  liveTakeover: Type.Boolean(),
});
export type ConversationListItemResponse = Static<typeof ConversationListItemSchema>;

export const ConversationListPageSchema = Type.Object({
  items: Type.Array(ConversationListItemSchema),
  total: Type.Number(),
  limit: Type.Number(),
  offset: Type.Number(),
  hasMore: Type.Boolean(),
});

export const ProviderMessageSchema = Type.Object({
  id: Type.Number(),
  role: MessageRoleSchema,
  authorName: Type.Union([Type.String(), Type.Null()]),
  content: Type.String(),
  flaggedAsUnanswered: Type.Boolean(),
  createdAt: Type.String(),
});

export const ConversationDetailPayloadSchema = Type.Object({
  conversation: ConversationListItemSchema,
  messages: Type.Array(ProviderMessageSchema),
});

// ── Provider dashboard overview ──────────────────────────────────────────

export const DashboardPayloadSchema = Type.Object({
  conversations: Type.Object({
    total: Type.Number(),
    active: Type.Number(),
    closed: Type.Number(),
    flagged: Type.Number(),
    withUnanswered: Type.Number(),
    last7d: Type.Number(),
    prev7d: Type.Number(),
  }),
  recentConversations: Type.Array(ConversationListItemSchema),
  liveConversation: Type.Union([ConversationListItemSchema, Type.Null()]),
  unansweredPending: Type.Number(),
  usage: Type.Object({
    yearMonth: Type.String(),
    plan: PlanSchema,
    planLabel: Type.String(),
    planPriceEur: Type.Number(),
    messagesUsed: Type.Number(),
    messagesLimit: Type.Number(),
  }),
  assistants: Type.Array(
    Type.Object({
      id: Type.Number(),
      name: Type.String(),
      status: AssistantStatusSchema,
      conversationCount: Type.Number(),
    }),
  ),
});

export const ConversationsQuerySchema = Type.Object({
  assistantId: Type.Optional(Type.Integer({ minimum: 1 })),
  status: Type.Optional(ConversationStatusSchema),
  limit: Type.Optional(Type.Integer({ minimum: 1, maximum: CONVERSATIONS_PAGE_MAX })),
  offset: Type.Optional(Type.Integer({ minimum: 0 })),
});
export type ConversationsQuery = Static<typeof ConversationsQuerySchema>;

export const ProviderMessageBodySchema = Type.Object({
  content: Type.String({ minLength: 1, maxLength: PROVIDER_MESSAGE_MAX_LENGTH }),
});
export type ProviderMessageBody = Static<typeof ProviderMessageBodySchema>;

// ── Unanswered questions (« colles ») ────────────────────────────────────

const UnansweredStatusSchema = Type.Union(UNANSWERED_STATUSES.map((s) => Type.Literal(s)));

export const UnansweredQuestionViewSchema = Type.Object({
  id: Type.Number(),
  assistantId: Type.Number(),
  assistantName: Type.String(),
  conversationId: Type.Number(),
  messageId: Type.Number(),
  questionText: Type.String(),
  assistantReply: Type.Union([Type.String(), Type.Null()]),
  status: UnansweredStatusSchema,
  providerAnswer: Type.Union([Type.String(), Type.Null()]),
  answeredAt: Type.Union([Type.String(), Type.Null()]),
  createdAt: Type.String(),
  conversationActive: Type.Boolean(),
});

export const UnansweredQuerySchema = Type.Object({
  assistantId: Type.Optional(Type.Integer({ minimum: 1 })),
  status: Type.Optional(UnansweredStatusSchema),
});
export type UnansweredQuery = Static<typeof UnansweredQuerySchema>;

// The answer reaches the visitor as a `provider` message, so it shares that
// length cap.
export const AnswerUnansweredBodySchema = Type.Object({
  answer: Type.String({ minLength: 1, maxLength: PROVIDER_MESSAGE_MAX_LENGTH }),
});
export type AnswerUnansweredBody = Static<typeof AnswerUnansweredBodySchema>;

// ── Administration (single hard-coded admin — CLAUDE.md §6) ──────────────

const AdminStatsSchema = Type.Object({
  accounts: Type.Number(),
  assistants: Type.Number(),
  conversations: Type.Number(),
  conversationsActive: Type.Number(),
  conversations7d: Type.Number(),
  messages: Type.Number(),
  tokensInput: Type.Number(),
  tokensOutput: Type.Number(),
  estimatedCostEur: Type.Number(),
  byPlan: Type.Array(Type.Object({ plan: PlanSchema, count: Type.Number() })),
});

const AdminAccountRowSchema = Type.Object({
  id: Type.Number(),
  email: Type.String(),
  displayName: Type.String(),
  plan: PlanSchema,
  isAdmin: Type.Boolean(),
  createdAt: Type.String(),
  assistantCount: Type.Number(),
  conversationCount: Type.Number(),
  messageCount: Type.Number(),
  tokensInput: Type.Number(),
  tokensOutput: Type.Number(),
  estimatedCostEur: Type.Number(),
  lastActivityAt: Type.Union([Type.String(), Type.Null()]),
});

const AdminConversationRowSchema = Type.Object({
  id: Type.Number(),
  accountId: Type.Number(),
  accountEmail: Type.String(),
  assistantId: Type.Number(),
  assistantName: Type.String(),
  status: ConversationStatusSchema,
  messageCount: Type.Number(),
  startedAt: Type.String(),
  lastMessageAt: Type.String(),
});

export const AdminOverviewPayloadSchema = Type.Object({
  stats: AdminStatsSchema,
  accounts: Type.Array(AdminAccountRowSchema),
  recentConversations: Type.Array(AdminConversationRowSchema),
});

export const ImpersonateResponseSchema = Type.Object({
  token: Type.String(),
  account: Type.Object({
    id: Type.Number(),
    email: Type.String(),
    displayName: Type.String(),
  }),
});

export const AdminAccountParamsSchema = Type.Object({
  id: Type.Integer({ minimum: 1 }),
});
